<template>
  <div class="inventory-table table-component">
    <artisio-table ref="inventoryTable" :pagination="tableData.pagination" :selectable="selectable"
                   :sort="tableData.ordering" :items="tableData.rows" :busy="tableData.loading"
                   :checkboxes="true" :search="tableData.search" :enable-simple-search="false" :fields="tableFields"
                   show-toggle-columns :extra-data="extraData"
                   @paramsChanged="getTableData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns" filter-toggle-string="inventory-table"
                   @selectedItemsChange="onSelectedItemsChange"
                   export-vuex-module-name="inventory" :enable-frontend-export="enableFrontendExport">

      <!--      <template v-slot:before-search-placeholder>-->
      <!--        <submit-button v-if="showQrCodesExport && tableData.rows.length" v-permission="'export_inventory'"-->
      <!--                       @click="onQrCodesExportClick" variant="secondary" class="shadow-none mr-2"-->
      <!--                       :show-loader="qrExportBtnLoading">-->
      <!--          <font-awesome-icon icon="qrcode" class="icon"/>-->
      <!--          {{ $t('Export QR Codes') }}-->
      <!--        </submit-button>-->
      <!--      </template>-->

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="inventory-table">
          <inventory-search :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" class="mr-2" v-if="!hideActionsDropdown"
                    v-permission="'change_inventoryitem'">
          <b-dropdown-item @click="onCreateAndDuplicateClick(selectedItems)"
                           :disabled="!(selectedItems.size === 1)"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_CREATE_AND_DUPLICATE)">
            {{ $t('Create and Duplicate') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onGroupItemsClick(selectedItems)"
                           :disabled="selectedItems.size === 0"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_GROUP_ITEMS) && enableGroupedItems">
            {{ $t('Group Items') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onContinuousAuctionBatchLotUpClick(selectedItems)"
                           :disabled="selectedItems.size === 0"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_CONT_AUCTION_BATCH_LOT_UP) && enableContinuousAuction">
            {{ $t('Batch Lot Up') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onBulkEditClick(selectedItems)"
                           :disabled="selectedItems.size === 0"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_BULK_EDIT)">
            {{ $t('Bulk Edit') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onCreateReturnNotesClick(selectedItems)"
                           :disabled="selectedItems.size === 0" v-permission="'change_inventoryitem'"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_RETURN_ITEMS)">
            {{ $t('Return Items') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onBulkRelocateClick(selectedItems)"
                           :disabled="selectedItems.size === 0"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_BULK_RELOCATE)">
            {{ $t('Relocate Items') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewQrCodesExportClick(selectedItems)"
                           :disabled="selectedItems.size === 0"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_GENERATE_BULK_QR_CODES)">
            {{ $t('Generate QR Codes') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onWebappQrCodesGenerate(selectedItems)"
                           :disabled="selectedItems.size === 0"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_GENERATE_WEBAPP_QR_CODES) && inventoryQrCodeForWebappURL && hasArtisioTimedProvider">
            {{ $t('Generate Public QR for Webapp') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onGenerateTransferNoteClick(selectedItems)"
                           :disabled="selectedItems.size === 0" v-permission="'change_transfernote'"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_GENERATE_TRANSFER_NOTE)">
            {{ $t('Generate Transfer Note') }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="tableData.loading" @click="onSyncClick"
                           v-permission="'change_auction'"
                           v-if="hasDataCollectionProvider && !excludeHeaderActions.includes(ACTION_INVENTORY_SYNC_WITH_FULCRUM)">
            {{ $t('Sync with Fulcrum') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onBatchUpdateReceiptClick(selectedItems)"
                           :disabled="selectedItems.size === 0" v-permission="'change_inventoryitem'"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_BATCH_UPDATE_RECEIPT)">
            {{ $t('Move Item(s) to Another Receipt') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onTransferAcrossBranchesClick(selectedItems)"
                           :disabled="selectedItems.size === 0" v-permission="'change_inventoryitem'"
                           v-if="usersOtherBranches.length !== 0 && !excludeHeaderActions.includes(ACTION_INVENTORY_TRANSFER_ACROSS_BRANCHES)">
            {{ $t('Move Item(s) to Another Branch') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item v-permission="'delete_inventoryitem'"
                           :disabled="!(searchModel && selectedItems.size > 0 && searchModel.is_archived)"
                           @click="onBatchUnarchiveClick(selectedItems)"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_UNARCHIVE)">
            {{ $t('Unarchive') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'delete_inventoryitem'"
                           :disabled="!(searchModel && selectedItems.size > 0 && !searchModel.is_archived)"
                           @click="onBatchArchiveClick(selectedItems)" variant="danger"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_ARCHIVE)">
            {{ $t('Archive') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item @click="onInventoryOverviewExportClick(selectedItems)" v-permission="'change_inventoryitem'"
                           :disabled="inventoryOverviewPrintsHpiData && !(searchModel && selectedItems.size > 0 && !searchModel.is_archived)"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW)">
            {{ $t('Inventory Overview') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item v-permission="'delete_inventoryitem'" :disabled="selectedItems.size === 0"
                           @click="onBatchRemoveClick(selectedItems)" variant="danger"
                           v-if="!excludeHeaderActions.includes(ACTION_INVENTORY_BATCH_DELETE)">
            {{ $t('Delete') }}
          </b-dropdown-item>
        </b-dropdown>
        <searchable-button-widget :model="model" attribute="sale_no" value-field="sale_no" class="mr-2"
                                  @select="reserveSale($event,selectedItems)" :label="false"
                                  :disabled="selectedItems.size === 0" v-permission="'change_inventoryitem'"
                                  :options="{placeholder: $t('Assign Sale'), dropdownAutoWidth: true}"
                                  :search-handler="assignSaleSearchHandler"
                                  v-if="!enableContinuousAuction && showAssignToSale"
                                  :display-field="assignSaleItemsDisplayFieldFunction"/>
        <add-new-button class="ml-2"
                        v-b-tooltip.hover="exportSelectedImagesTooltip(selectedItems)"
                        v-permission="'export_inventory'"
                        :disabled="!isExportSelectedImagesEnabled(selectedItems)"
                        @click="onExportSelectedImagesClick(selectedItems)">
          {{ $t('Export Selected Images') }}
        </add-new-button>
      </template>

      <template slot="cell(image)" slot-scope="{item: inventory}" class="text-center">
        <list-image-uploader :inventory="inventory" @imagesUploaded="onImagesUploaded"/>
      </template>

      <template slot="cell(lot_no)" slot-scope="{item: inventory}" class="text-center">
        <span v-if="inventory.latest_reservation">
          {{ inventory.latest_reservation.lot_no }}
        </span>
      </template>

      <template slot="cell(hammer_price)" slot-scope="{item: inventory}" class="text-center">
        <span v-if="inventory.latest_reservation">
          {{ inventory.latest_reservation.hammer_price | asCurrency }}
        </span>
      </template>


      <template slot="cell(collected)" slot-scope="{item: inventory}">
        <span v-if="inventory.collected" class="badge badge-success">{{ $t('Yes') }}</span>
        <span v-else class="badge badge-danger">{{ $t('No') }}</span>
      </template>

      <template slot="cell(reserve_type)" slot-scope="{item: inventory}">
        {{ getReserveType(inventory.reserve_type) }}
      </template>

      <template slot="cell(title)" slot-scope="{item: inventory}">
        <div v-if="inventory.is_draft">
          <span class="badge badge-secondary">{{ $t('Draft') }}</span>
        </div>
        {{ inventory.dynamic_fields | attributeByMasterLanguage('title') | striptags }}
      </template>

      <template slot="cell(stock_no)" slot-scope="{item: inventory}">
        <template v-if="inventory.receipt">
          <router-link v-if="$hasPermission('view_receipt')"
                       :to="{name: 'receipt.dashboard', params: {id: inventory.receipt_uuid}}">
            {{ inventory.receipt.receipt_no }}
          </router-link>
          <span v-else class="m-0"> {{ inventory.receipt.receipt_no }} </span>
          / {{ inventory.item_no }}
          <grouped-item-indicator :inventory="inventory"/>
          <action-buttons
            :view="!excludeItemActionButtons.includes('view')"
            :edit="!excludeItemActionButtons.includes('edit')"
            :remove="!excludeItemActionButtons.includes('remove')"
            :delete-permission="'delete_inventoryitem'"
            :edit-permission="'change_inventoryitem'"
            :view-permission="'view_inventoryitem'"
            @viewClick="onViewClick(inventory)"
            @editClick="onEditClick(inventory)"
            @deleteClick="onRemoveClick(inventory)"
            @removeSourceItemClick="onRemoveSourceItemClick(inventory)"
            :extra-actions="computedExtraTableAction"/>
        </template>
      </template>
      <template slot="cell(reserve)" slot-scope="{item: inventory}">
        {{ inventory.reserve | asCurrency }}
      </template>
      <template slot="cell(insurance)" slot-scope="{item: inventory}">
        {{ inventory.insurance | asPercentage }}
      </template>
      <template slot="cell(start_price)" slot-scope="{item: inventory}">
        {{ inventory.start_price | asCurrency }}
      </template>
      <template slot="cell(branch_uuid)" slot-scope="{item: inventory}">
        <template v-if="inventory.branch">
          <router-link :to="{name: 'branches', params: {branch_uuid: inventory.branch_uuid}}">
            {{ inventory.branch.name }}
          </router-link>
        </template>
      </template>
      <template slot="cell(low)" slot-scope="{item: inventory}">
        <template v-if="inventory.low || inventory.high">
          {{ inventory.low | asCurrency }} - {{ inventory.high | asCurrency }}
        </template>
      </template>
      <template slot="cell(account_managers)" slot-scope="{item: inventory}">
        <template v-if="inventory.account_managers && inventory.account_managers.length > 0">
          {{ inventory.account_managers[0].display_name }}
        </template>
      </template>
      <template slot="cell(receive_date)" slot-scope="{item: inventory}">
        {{ inventory.receive_date | toDate }}
      </template>
      <template slot="cell(customer)" slot-scope="{item: inventory}">
        {{ inventory.customer.display_name_with_company }}
      </template>
      <template slot="cell(tax_status)" slot-scope="{item: inventory}">
        {{ inventory.tax_status_uuid | asTaxStatus }}
      </template>
      <template slot="cell(department)" slot-scope="{item: inventory}">
        {{ inventory.department.name | masterLanguage }}
      </template>
      <template slot="cell(category)" slot-scope="{item: inventory}">
        {{ inventory.category.constructed_full_path | masterLanguage }}
      </template>
      <template slot="cell(item_location)" slot-scope="{item: inventory}">
        <span>{{ getItemLocationComputedName(inventory) }}</span>
        <a href="#" v-if="priorMoveCount(inventory) > 0" class="ml-1 badge badge-light border"
           @click.prevent="onMovementHistoryBadgeClick(inventory)">
          +{{ priorMoveCount(inventory) }}
        </a>
      </template>
      <template slot="cell(collection_info)" slot-scope="{item: inventory}">
        <div v-if="inventory.collection_info_full"
             v-html="$options.filters.splitAddressForMultiline(inventory.collection_info_full)">
        </div>
      </template>
      <template slot="cell(customer)" slot-scope="{item: inventory}">
        <router-link v-if="$hasPermission('view_customer')"
                     :to="{name: 'customers.dashboard', params: { id: inventory.customer.uuid }}">
          {{ inventory.customer.display_name_with_company }}
        </router-link>
        <span v-else>
          {{ inventory.customer.display_name_with_company }}
        </span>
      </template>
      <template slot="cell(created_at)" slot-scope="{item: inventory}">
        {{ inventory.created_at | toDate }} by {{ inventory.created_by.display_name }}
      </template>
      <template slot="cell(updated_at)" slot-scope="{item: inventory}">
        {{ inventory.updated_at | toDate }} by {{ inventory.updated_by.display_name }}
      </template>
      <template slot="cell(rep_user)" slot-scope="{item: inventory}">
        <span v-if="inventory.rep_user">{{ inventory.rep_user.display_name }}</span>
      </template>
      <template slot="cell(grouped_item_no)" slot-scope="{item: inventory}">
        <span
          v-if="!inventory.is_grouped_item  && inventory.grouped_inventories_count && inventory.grouped_inventories_count > 0">
          <span v-for="(i, idx) in inventory.grouped_inventories">
            <template v-if="i.grouped_inventory">
              {{ i.grouped_inventory.item_no }}<span v-if="idx < inventory.grouped_inventories.length - 1">,</span>
            </template>
          </span>
        </span>
      </template>
      <template slot="cell(company_name)" slot-scope="{item: inventory}">
        <span v-if="inventory && inventory.customer">
          {{ inventory.customer.company_name }}
        </span>
      </template>
      <template slot="cell(specialists)" slot-scope="{item: inventory}">
        <template v-if="inventory.specialists">
          {{ inventory.specialists.map(s => s.display_name).join(', ') }}
        </template>
      </template>
      <template slot="cell(cataloguing_lifecycle)" slot-scope="{item: inventory}">
        <template v-if="inventory.cataloguing_lifecycle">
          <cataloguing-lifecycle-display-widget :inventory="inventory"/>
        </template>
      </template>
      <template slot="cell(photography_lifecycle)" slot-scope="{item: inventory}">
        <template v-if="inventory.photography_lifecycle">
          <photography-lifecycle-display-widget :inventory="inventory"/>
        </template>
      </template>
      <template slot="cell(return_note_collector)" slot-scope="{item: inventory}">
        <template v-if="inventory.return_notes && inventory.return_notes.length > 0">
          {{ inventory.return_notes[0].collector }}
        </template>
      </template>
      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span
          v-html="$options.filters.asDynamicField(getDynamicFieldValue(item, dynamicField), field.type, field.choices)">
        </span>
      </template>
      <template slot="cell(status)" slot-scope="{item: inventory}">
        <inventory-status :inventory="inventory"/>
      </template>
      <template slot="cell(collected_status)" slot-scope="{item: inventory}">
        <template v-if="inventory.collected_status && inventory.collected_status.length > 0">
          <span v-as-status="statusObj.status" v-for="statusObj in inventory.collected_status"
                :key="`inventory-collected-status-${inventory.uuid}-${statusObj.status}`" class="mr-2">
            {{ statusObj.status | removeUnderscores | toUpperCaseEveryWord | translate }} ({{ statusObj.quantity }})
          </span>
        </template>
      </template>
      <template slot="cell(condition_requests_count)" slot-scope="{item: inventory}">
        <span v-if="inventory">
          {{ inventory.condition_requests_count }}
        </span>
      </template>
      <template slot="cell(condition_requests_sent)" slot-scope="{item: inventory}">
        <span v-if="inventory">
          {{ inventory.condition_requests_sent }}
        </span>
      </template>
      <template slot="cell(dpv_pdf)" slot-scope="{item: inventory}">
        <b-button v-if="inventory && inventory.dpv_pdf" @click="onViewDpvPdfClick(inventory.dpv_pdf)" variant="primary"
                  size="sm" class="mr-2">
          <font-awesome-icon icon="download"/>
        </b-button>
      </template>
      <template slot="cell(proposal_suggestion)" slot-scope="{item: inventory}">
        <span v-if="inventory.latest_reservation && inventory.latest_reservation.proposal_suggestion"
              v-as-status="inventory.latest_reservation.proposal_suggestion">
          {{ inventory.latest_reservation.proposal_suggestion | proposalStatusSuggestionMap  | translate }}
        </span>
      </template>
      <template slot="cell(proposal_status)" slot-scope="{item: inventory}">
        <span v-if="inventory.latest_reservation && inventory.latest_reservation.proposal_status"
              v-as-status="`proposal_${inventory.latest_reservation.proposal_status}`">
          {{ inventory.latest_reservation.proposal_status | proposalStatusSuggestionMap | translate }}
        </span>
      </template>
      <template slot="cell(transfer_info)" slot-scope="{item: inventory}">
        <transfer-note-short-info :item="inventory" :source="SOURCE_INVENTORY_TABLE"/>
      </template>
      <template slot="cell(internal_images)" slot-scope="{item: inventory}">
        <div class="position-relative">
          <internal-images-viewer :internal-images="inventory.internal_images"
                                  v-if="inventory.internal_images && inventory.internal_images.length > 0"/>
          <div class="attachments-numbering" v-if="inventory.internal_images && inventory.internal_images.length > 1">
            +{{ inventory.internal_images.length - 1}}
          </div>
        </div>
      </template>
      <template slot="cell(attachments)" slot-scope="{item: inventory}">
        <a href="javascript:void(0)" @click="onAttachmentsViewClick(inventory)"
           :class="!inventory.attachments || inventory.attachments.length === 0 ? 'text-muted' : ''"
           v-if="$hasPermission('view_inventoryitem')">
          {{ $t('{count} attachment(s)', {count: inventory.attachments ? inventory.attachments.length : 0}) }}
        </a>
      </template>
    </artisio-table>
    <transfer-across-branches-modal/>
    <batch-update-receipt-modal/>
    <movement-history-modal :visible="movementHistoryModal.visible"
                            :inventory-uuid="movementHistoryModal.inventoryUuid"
                            @hidden="movementHistoryModal.visible = false"/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import InventoryService from "./InventoryService";
  import {
    ACTION_INVENTORY_CONT_AUCTION_BATCH_LOT_UP,
    ACTION_INVENTORY_CREATE_AND_DUPLICATE,
    ACTION_INVENTORY_BATCH_DELETE,
    ACTION_INVENTORY_GROUP_ITEMS,
    ACTION_INVENTORY_SYNC_WITH_FULCRUM,
    ACTION_INVENTORY_BULK_EDIT,
    SOURCE_INVENTORY_TABLE,
    ACTION_INVENTORY_REMOVE_SOURCE_ITEM,
    ACTION_INVENTORY_ARCHIVE,
    ACTION_INVENTORY_UNARCHIVE,
    ACTION_INVENTORY_RETURN_ITEMS,
    ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW,
    ACTION_INVENTORY_BULK_RELOCATE,
    ACTION_INVENTORY_GENERATE_BULK_QR_CODES,
    ACTION_INVENTORY_GENERATE_TRANSFER_NOTE,
    ACTION_INVENTORY_TRANSFER_ACROSS_BRANCHES,
    ACTION_INVENTORY_BATCH_UPDATE_RECEIPT,
    ACTION_INVENTORY_GENERATE_WEBAPP_QR_CODES,
  } from "../constants";
  import ActionButtons from "@/core/components/ams-table/ActionButtons";
  import InventorySearch from "./InventorySearch";
  import SearchCollapse from "@/core/components/search-collapse/SearchCollapse";
  import ListImageUploader from "./ListImageUploader";
  import ReceiptService from "@/modules/receipt/ReceiptService";
  import {
    ON_PDF_MODAL_DATA_RETRIEVE,
    ON_PDF_MODAL_OPEN,
    ON_QR_CODE_READER_MODAL_OPEN
  } from "@/plugins/event-bus/event-bus-names";
  import InventoryModel from "@/modules/inventory/Inventory.model";
  import SearchableButtonWidget from "@/core/widgets/SearchableButtonWidget";
  import SalesService from "@/modules/sales/SalesService";
  import {Formatter} from "@/core/services/formatter.service";
  import {WS_DATA_COLLECTION_PROVIDER_PROGRESS, WS_TRANSFER_ACROSS_BRANCHES_PROGRESS, WS_BATCH_UPDATE_RECEIPT_PROGRESS} from "@/modules/constants";
  import {ProgressMixin} from "@/core/mixins/progress.mixin";
  import _ from "lodash";
  import InventoryStatus from "@/modules/inventory/components/InventoryStatus";
  import GroupedItemIndicator from "@/modules/inventory/GroupedItemIndicator";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";
  import DpvService from "@/modules/dpv/DpvService";
  import cloneDeep from "lodash/cloneDeep";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";
  import TransferNoteShortInfo from "@/modules/inventory/components/TransferNoteShortInfo";
  import TransferAcrossBranchesModal from "./transfer-across-branches/TransferAcrossBranchesModal";
  import BatchUpdateReceiptModal from "./batch-update-receipt/BatchUpdateReceiptModal";
  import CataloguingLifecycleDisplayWidget from "@/core/widgets/CataloguingLifecycleDisplayWidget.vue";
  import PhotographyLifecycleDisplayWidget from "@/core/widgets/PhotographyLifecycleDisplayWidget.vue";
  import ItemLocationService from "@/modules/setup/branch/ItemLocationService";
  import AddNewButton from "@/core/components/common/AddNewButton";
  import i18n from "@/shared/i18n";
  import InternalImagesViewer from "@/core/components/attachment-image-viewer/InternalImagesViewer.vue";
  import MovementHistoryModal from "@/modules/inventory/view/movement-history/MovementHistoryModal";

  const {mapActions, mapState, mapGetters} = createNamespacedHelpers('inventory');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('lots');
  const {mapActions: collectionsMapActions} = createNamespacedHelpers('collections');
  const {mapState: globalMapState, mapActions: globalMapActions} = createNamespacedHelpers('global');
  const {mapState: receiptMapState} = createNamespacedHelpers('receipts');
  const {mapActions: transferNoteMapActions} = createNamespacedHelpers('transferNotes');
  const {mapState: mapStateDepartments} = createNamespacedHelpers('setup/department');

  export default {
    name: "InventoryTable",
    components: {
      AddNewButton,
      InternalImagesViewer,
      PhotographyLifecycleDisplayWidget,
      CataloguingLifecycleDisplayWidget,
      TransferNoteShortInfo,
      GroupedItemIndicator,
      InventoryStatus,
      SearchableButtonWidget,
      ListImageUploader,
      SearchCollapse,
      InventorySearch,
      ActionButtons,
      ArtisioTable,
      TransferAcrossBranchesModal,
      BatchUpdateReceiptModal,
      MovementHistoryModal
    },
    props: {
      selectable: {
        type: Boolean,
        default: true,
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      showQrCodesExport: {
        type: Boolean,
        default: false
      },
      showExportAllImages: {
        type: Boolean,
        default: false
      },
      showAssignToSale: {
        type: Boolean,
        default: true
      },
      excludeHeaderActions: {
        type: Array,
        default: () => []
      },
      excludeItemActionButtons: {
        type: Array,
        default: () => []
      },
      extraTableActions: {
        type: Array,
        default: () => []
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    mixins: [ProgressMixin],
    data: () => {
      return {
        qrExportBtnLoading: false,
        inventoryHasImages: false,
        model: new InventoryModel(),
        progress: null,
        ACTION_INVENTORY_BULK_EDIT: ACTION_INVENTORY_BULK_EDIT,
        ACTION_INVENTORY_BULK_RELOCATE: ACTION_INVENTORY_BULK_RELOCATE,
        ACTION_INVENTORY_GENERATE_BULK_QR_CODES: ACTION_INVENTORY_GENERATE_BULK_QR_CODES,
        ACTION_INVENTORY_GENERATE_WEBAPP_QR_CODES: ACTION_INVENTORY_GENERATE_WEBAPP_QR_CODES,
        ACTION_INVENTORY_RETURN_ITEMS: ACTION_INVENTORY_RETURN_ITEMS,
        ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW: ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW,
        ACTION_INVENTORY_CREATE_AND_DUPLICATE: ACTION_INVENTORY_CREATE_AND_DUPLICATE,
        ACTION_INVENTORY_GROUP_ITEMS: ACTION_INVENTORY_GROUP_ITEMS,
        ACTION_INVENTORY_CONT_AUCTION_BATCH_LOT_UP: ACTION_INVENTORY_CONT_AUCTION_BATCH_LOT_UP,
        ACTION_INVENTORY_SYNC_WITH_FULCRUM: ACTION_INVENTORY_SYNC_WITH_FULCRUM,
        ACTION_INVENTORY_BATCH_DELETE: ACTION_INVENTORY_BATCH_DELETE,
        ACTION_INVENTORY_ARCHIVE: ACTION_INVENTORY_ARCHIVE,
        ACTION_INVENTORY_UNARCHIVE: ACTION_INVENTORY_UNARCHIVE,
        ACTION_INVENTORY_GENERATE_TRANSFER_NOTE: ACTION_INVENTORY_GENERATE_TRANSFER_NOTE,
        ACTION_INVENTORY_TRANSFER_ACROSS_BRANCHES: ACTION_INVENTORY_TRANSFER_ACROSS_BRANCHES,
        ACTION_INVENTORY_BATCH_UPDATE_RECEIPT: ACTION_INVENTORY_BATCH_UPDATE_RECEIPT,
        SOURCE_INVENTORY_TABLE: SOURCE_INVENTORY_TABLE,
        transferAcrossBranchesProgress: null,
        EXPORT_SELECTED_IMAGES_LIMIT: 200,
        movementHistoryModal: {
          visible: false,
          inventoryUuid: null,
        },
      }
    },
    computed: {
      ...mapGetters({
        tableFields: 'tableFields',
        toggleColumns: 'toggleColumns',
      }),
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        upcomingSales: state => state.tableData[state.tableSource].upcomingSales,
        searchModel: state => state.tableData[state.tableSource].searchModel,
        tableSource: state => state.tableSource,
        currentInventory: state => state.view.currentInventory,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        hasDataCollectionProvider: state => state.data_collection_provider_info.has_data_collection_provider,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
        enableGroupedItems: state => state.enableGroupedItems,
        locales: state => state.locales,
        reserveTypes: state => state.reserveTypes,
        inventoryOverviewPrintsHpiData: state => state.inventoryOverviewPrintsHpiData,
        currentBranch: state => state.currentBranch,
        currentUser: state => state.currentUser,
        inventoryQrCodeForWebappURL: state => state.inventoryQrCodeForWebappURL,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
        itemLocations: state => state.itemLocations,
        auctionImageExportNamingFormat: state => state.auctionImageExportNamingFormat,
      }),
      ...receiptMapState({
        receipt: state => state.view.currentReceipt
      }),
      ...mapStateDepartments({
        departments: state => state.departments
      }),
      itemLocationMap() {
        if (!this.itemLocations || !this.itemLocations.length) return {};
        const map = {};
        const buildMap = (nodes) => {
          for (const node of nodes) {
            map[node.uuid] = node.computed_name;
            if (node.children && node.children.length) buildMap(node.children);
          }
        };
        buildMap(this.itemLocations);
        return map;
      },
      usersOtherBranches() {
        if (!this.currentUser || !this.currentUser.branches) {
          return [];
        }
        return this.currentUser.branches.filter(b => b.uuid !== this.currentBranch.uuid);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: cloneDeep(this.excludeColumns)
        }
      },
      hideActionsDropdown() {
        const actions = [
          ACTION_INVENTORY_CREATE_AND_DUPLICATE,
          ACTION_INVENTORY_GROUP_ITEMS,
          ACTION_INVENTORY_CONT_AUCTION_BATCH_LOT_UP,
          ACTION_INVENTORY_SYNC_WITH_FULCRUM,
          ACTION_INVENTORY_BATCH_DELETE,
        ];

        for (let key of actions) {
          if (!this.excludeHeaderActions.includes(key)) {
            return false
          }
        }

        return true;
      },
      computedExtraTableAction() {
        let arr = []
        if (this.extraTableActions.includes(ACTION_INVENTORY_REMOVE_SOURCE_ITEM)) {
          arr.push({
            index: 2,
            data: {
              text: this.$i18n.t('Remove'),
              emitKey: 'removeSourceItemClick',
              textClass: 'text-danger',
            }
          })
        }
        return arr;
      }
    },
    methods: {
      ...mapActions([
        'getTableData',
        'showModal',
        "showReserveSaleModal",
        'removeInventory',
        'removeSourceItem',
        'getUpcomingSales',
        'getModalObject',
        'updateSearchModel',
        'updateListInventoryImages',
        'batchRemoveItems',
        'batchArchiveItems',
        'batchUnarchiveItems',
        'updateToggleColumns',
        'showContinuousAuctionBatchLotUpModal',
        'getDataForContinuousAuctionBatchLotUpModal',
        'showGroupedItemsReserveModal',
        'showBulkEditModal',
        'showInventoryOverviewGenerationModal',
        'showTransferAcrossBranchesModal',
        'updateAttachmentsCurrentTab',
        'showBatchUpdateReceiptModal',
      ]),
      ...collectionsMapActions([
        'showCollectionsModal',
      ]),
      ...globalMapActions(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...lotsMapActions(['showLotImagesNameModal']),
      ...transferNoteMapActions({
        showTransferNoteModal: 'showModal',
        getItemsForCreateForTransferNote: 'getItemsForCreate',
      }),
      getItemLocationComputedName(inventory) {
        if (!inventory || !inventory.item_location_uuid) return '';
        return this.itemLocationMap[inventory.item_location_uuid] || (inventory.item_location ? inventory.item_location.name : '');
      },
      priorMoveCount(inventory) {
        const total = inventory && inventory.movement_history_count ? inventory.movement_history_count : 0;
        return total > 1 ? total - 1 : 0;
      },
      onMovementHistoryBadgeClick(inventory) {
        this.movementHistoryModal = {
          visible: true,
          inventoryUuid: inventory.uuid,
        };
      },
      async onViewClick(data) {
        this.$router.push({
          path: `/inventory/view/${data.uuid}`
        });
      },
      async onEditClick(item) {
        this.showModal();
        this.getModalObject({uuid: item.uuid, source: SOURCE_INVENTORY_TABLE});
      },
      async onRemoveClick(item) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following inventory item?"),
          this.$i18n.t('Inventory item was successfully deleted'),
          this.removeInventory,
          item
        );
      },
      async onRemoveSourceItemClick(item) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to remove following inventory item from this group?"),
          this.$i18n.t('Inventory item was removed from the group'),
          this.removeSourceItem,
          {
            grouped_inventory_uuid: this.currentInventory.uuid,
            source_inventory_uuid: item.uuid
          }
        );
      },
      getReserveType(reserveType) {
        const tmpType = this.reserveTypes.find(r => r.value === reserveType)
        return tmpType ? tmpType.key : ""
      },
      async reserveSale(sale, selectedItems) {
        let itemsToReserve = []
        selectedItems.forEach(item => {
          if (item.available_quantity) {
            itemsToReserve.push(item)
          }
        })
        if (itemsToReserve.length) {
          this.showReserveSaleModal({items: itemsToReserve, sale});
        } else {
          this.$errorDialog(this.$i18n.t('Following item(s) has already been reserved.'), this.$i18n.t('Assign Sale'));
        }
      },
      onImagesUploaded(data) {
        this.updateListInventoryImages(data);
      },
      // async onQrCodesExportClick() {
      //   this.qrExportBtnLoading = true;
      //   let url = await ReceiptService.getInventoryQrCodesUrl(this.tableData.extraFilters.receipt_uuid);
      //   this.qrExportBtnLoading = false;
      //   if (!url) {
      //     this.$errorDialog(this.$i18n.t("Error while generating export token. Please, try again later."));
      //     return true;
      //   }
      //   this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
      //     title: this.$i18n.t('Receipt {receipt} QR codes', {receipt: this.receipt.receipt_no}),
      //     data: {pdf_url: url}
      //   });
      // },
      async onNewQrCodesExportClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));
        this.qrCodeGenerationLoading = true;
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Inventory QR Codes')});
        let response = await QrCodeService.batchGenerateQrCode({model_type: 'inventoryitem', model_uuids: uuids})
        this.qrCodeGenerationLoading = false;
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            filename: this.$i18n.t('Inventory QR Codes'), pdf_url: response.body.url
          })
        }
      },
      async onWebappQrCodesGenerate(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));

        let response = await InventoryService.getQrCodeForWebapp({uuids});

        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
            title: this.$i18n.t('Inventory QR Codes for Webapp'),
          });
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            filename: this.$i18n.t('Inventory QR Codes for Webapp'),
            pdf_url: response.body.url
          })
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Inventory QR Codes for Webapp Validation'));
          }
        }
      },
      onExportAllImagesClick() {
        ReceiptService.getReceiptItemsImagesZipData(this.tableData.extraFilters.receipt_uuid);
      },
      async onBatchRemoveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to delete selected items?"));
        if (success) {
          let response = await this.batchRemoveItems(uuids)
          if (response.success) {
            this.$toast('success', this.$i18n.t('Item(s) were successfully deleted'));
            this.getTableData();
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Bulk Delete Validation'));
            } else if (response.body.errors) {
              const listItemsArr = [];
              response.body.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            }
          }
        }
      },
      async onBatchArchiveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push({uuid: item.uuid}));

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to archive selected items?"));
        if (success) {
          let response = await this.batchArchiveItems(uuids)
          if (response.success) {
            this.$toast('success', this.$i18n.t('Item(s) were successfully archived'));
            this.getTableData();
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Bulk Archive Validation'));
            } else if (response.body.errors) {
              const listItemsArr = [];
              response.body.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            }
          }
        }
      },
      async onBatchUnarchiveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push({uuid: item.uuid}));

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to unarchive selected items?"));
        if (success) {
          let response = await this.batchUnarchiveItems(uuids)
          if (response.success) {
            this.$toast('success', this.$i18n.t('Item(s) were successfully unarchived'));
            this.getTableData();
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Bulk Unarchive Validation'));
            } else if (response.body.errors) {
              const listItemsArr = [];
              response.body.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            }
          }
        }
      },
      async onGenerateTransferNoteClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));

        if (uuids.length > 100) {
          this.$errorDialog(this.$i18n.t('You cannot add more than 100 items under single transfer note'))
          return
        }

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to generate transfer note for selected items?"));
        if (success) {
          this.showTransferNoteModal({source: SOURCE_INVENTORY_TABLE})
          this.getItemsForCreateForTransferNote({uuids: uuids.join(','), source: SOURCE_INVENTORY_TABLE})
        }
      },
      assignSaleSearchHandler(search) {
        return SalesService.getSales({
          search,
          status_in: 'draft,published,listed',
          expand: 'currency',
          ordering: 'start_date'
        });
      },
      hasImages() {
        if (this.tableData && this.tableData.rows) {
          let inventories = this.tableData.rows.filter(item => item.images && item.images.length);
          this.inventoryHasImages = !!inventories.length;
        }
      },
      assignSaleItemsDisplayFieldFunction(item) {
        let saleNo = item.sale_no;
        let title = this.$options.filters.truncate(Formatter.getByMasterLanguage(item.title), 25)
        return `${saleNo} - ${title}`
      },
      async onSyncClick() {
        const {success} = await InventoryService.syncInventoryItemsWithThirdParty();
        if (success) {
          await this.addProgressIfNotExist('Data collection started');
        }
      },
      async onProgressNotificationReceive(data) {
        await this.handleProgressNotification(data,
          'Data Collection successfully finished',
          'Validation Error',
          'Something went wrong while running data collection. Please, try again later.');
      },
      onCreateAndDuplicateClick(selectedItems) {
        const items = _.cloneDeep(selectedItems);
        if (items.size === 1) {
          items.forEach(item => {
            this.showModal();
            this.getModalObject({uuid: item.uuid, source: SOURCE_INVENTORY_TABLE, isDuplicate: true});
          });
        }
      },
      onBulkEditClick(selectedItems) {
        if (selectedItems.size > 200) {
          this.$errorDialog(this.$i18n.t("You can not edit more than {num} items.", {num: 200}))
          return false;
        }

        let uuids = [];
        selectedItems.forEach(item => uuids.push(item.uuid));
        this.showBulkEditModal({source: SOURCE_INVENTORY_TABLE, uuids});
      },
      onBulkRelocateClick(selectedItems) {
        if (selectedItems.size > 200) {
          this.$errorDialog(this.$i18n.t("You can not relocate more than {num} items.", {num: 200}))
          return false;
        }

        let uuids = [];
        selectedItems.forEach(item => uuids.push(item.uuid));

        this.$evBus.$emit(ON_QR_CODE_READER_MODAL_OPEN, {
          handler: async (result) => {

            let response = await QrCodeService.getQrCodeInfo(result)

            if (response.success) {
              if (response.body.result && response.body.result.model_type === 'itemlocation') {

                let saveResponse = await InventoryService.bulkRelocate({
                  item_location_uuid: response.body.result.model_uuid,
                  inventory_uuids: uuids
                });

                if (saveResponse.success) {
                  this.$toast("success", this.$i18n.t("Items have been successfully relocated"));
                  this.getTableData();
                } else {
                  if (saveResponse.body.non_field_errors) {
                    this.$errorDialog(saveResponse.body.non_field_errors[0]);
                  } else if (saveResponse.toastError) {
                    this.$errorDialog(saveResponse.toastError);
                  }
                }

              } else {
                this.$errorDialog(this.$i18n.t("Invalid QR code provided"))
              }
            } else {
              if (response.body.non_field_errors) {
                this.$errorDialog(response.body.non_field_errors[0]);
              } else if (response.toastError) {
                this.$errorDialog(response.toastError)
              }
            }
          }
        });
      },
      onGroupItemsClick(selectedItems) {
        if (selectedItems.size > 50) {
          this.$errorDialog(this.$i18n.t("You can not group more than {num} items.", {num: 50}))
          return false;
        }

        let items = []
        let uniqueCustomerUuids = []

        for (let i of selectedItems) {
          if (i.is_grouped_item) {
            this.$errorDialog(this.$i18n.t("You can not group grouped item. Please, deselect grouped item and try again."))
            return false;
          }

          if (!uniqueCustomerUuids.includes(i.customer_uuid)) {
            uniqueCustomerUuids.push(i.customer_uuid)
          }
          if (uniqueCustomerUuids.length > 1) {
            this.$errorDialog(this.$i18n.t("Items belonging to multiple sellers, cannot be grouped together."))
            return false
          }

          if (i.available_quantity) {
            items.push(i)
          }
        }


        if (items.length === 0) {
          this.$errorDialog(this.$i18n.t("Selected inventory items have no available quantity. Please, select items with available quantity."))
          return false;
        }

        this.showGroupedItemsReserveModal({items, source: SOURCE_INVENTORY_TABLE});
      },
      getDynamicFieldValue(item, field) {
        return this.$options.filters.masterLanguage(item.dynamic_fields)[field]
      },
      isExportSelectedImagesEnabled(selectedItems) {
        return selectedItems.size >= 1 && selectedItems.size <= this.EXPORT_SELECTED_IMAGES_LIMIT;
      },
      exportSelectedImagesTooltip(selectedItems) {
        if (selectedItems.size === 0) {
          return this.$i18n.t('Select at least one inventory item to export images.');
        }
        if (selectedItems.size > this.EXPORT_SELECTED_IMAGES_LIMIT) {
          return this.$i18n.t('Select at most {limit} items.', {limit: this.EXPORT_SELECTED_IMAGES_LIMIT});
        }
        return '';
      },
      onExportSelectedImagesClick(selectedItems) {
        if (!this.isExportSelectedImagesEnabled(selectedItems)) {
          return;
        }
        const items = [...selectedItems];
        const inventory_uuids = items.map(item => item.uuid);
        const count = inventory_uuids.length;

        const msg = this.$i18n.t(
          "Export may take time, depending on the size of the data. You are about to export images for {count} selected inventory item(s). Are you sure you want to proceed?",
          {count}
        );
        this.$confirm(
          msg,
          this.$i18n.t("Image export links are being generated please check back soon."),
          (event) => InventoryService.batchDownloadImages(event),
          {inventory_uuids, file_name_template: null},
          i18n.t('Please Confirm'),
          i18n.t('Generate Image Exports'),
          i18n.t("Cancel"),
        );
      },
      onSelectedItemsChange(selectedItems) {
        this.$emit('selectedItemsChange', selectedItems);
      },
      onContinuousAuctionBatchLotUpClick(selectedItems) {
        let uuids = [];
        selectedItems.forEach(i => {
          if (i.available_quantity > 0) {
            uuids.push(i.uuid)
          }
        });

        if (uuids.length > 50) {
          this.$errorDialog(this.$i18n.t("Only 50 items can be lotted up at once. Please, deselect the items and try again."))
          return;
        } else if (uuids.length === 0) {
          this.$errorDialog(this.$i18n.t("Selected items do not have sufficient quantity. Please, select items with available quantity."))
          return;
        }

        this.showContinuousAuctionBatchLotUpModal({
          source: SOURCE_INVENTORY_TABLE
        });
        this.getDataForContinuousAuctionBatchLotUpModal(uuids)
      },
      async onCreateReturnNotesClick(items) {
        let success = await this.$confirm(this.$i18n.t("Are you sure you want to return following items?"))
        if (!success) {
          return;
        }

        let inventoryUuids = []
        let receiptUuid = null;
        let customerUuid = null;
        for (let i of items) {
          receiptUuid = i.receipt_uuid
          inventoryUuids.push({"inventory_uuid": i.uuid})

          if (customerUuid && customerUuid !== i.customer_uuid) {
            this.$errorDialog(this.$i18n.t("You can not return items for different customers simultaneously"));
            return;
          }
          customerUuid = i.customer_uuid
        }

        const reservationProposals = (await LotsService.getLatestReservationProposals({
          inventory_uuids: inventoryUuids.map(i => i.inventory_uuid),
          suggestions: ['reoffer'],
        })).body

        if (reservationProposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservationProposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) return
        }

        if (this.departments && this.departments.rows && this.departments.rows.length > 0) {
          let tmpItems = []
          items.forEach(i => {
            let tmpDept = this.departments.rows.find(d => d.uuid === i.department_uuid)
            if (!tmpDept) return
            tmpItems.push({department: tmpDept, stock_no: i.stock_no, dynamic_fields: i.dynamic_fields})
          })

          let tmpSuccess = await InventoryService.warnOnCollectionForDynamicFields(this, tmpItems, false)
          if (!tmpSuccess) {
            return
          }
        }

        let requestData = {
          "return_note_inventories": inventoryUuids,
          "description": "",
          "receipt_uuid": receiptUuid
        }

        let handler = async (data) => {
          let response = await InventoryService.createReturnNotes({
            ...requestData,
            ...data
          }, 'receipt.customer.billing_address');
          if (response.success) {
            this.$toast('success', this.$i18n.t('Return note was successfully created'))
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0])
            }
          }
          return response
        }

        this.showCollectionsModal({
          customer_uuid: customerUuid,
          source: 'return_note_contract',
          handler: handler,
          title: this.$i18n.t('Return Note')
        })
      },
      getInventoryTablePayload() {
        let payload = {}
        if (this.searchModel) {
          payload = this.searchModel.toJSON();
          for (let key in payload) {
            if (!payload[key]) {
              delete payload[key]
            }
          }
        }
        return payload
      },
      async onInventoryOverviewExportClick(items) {
        let payload;
        if (!this.inventoryOverviewPrintsHpiData) {
          if (this.tableData && this.tableData.rows.length === 0) {
            this.$toast('info', this.$i18n.t('There is no data to export. Please, change filter configuration and try again.'))
            return
          }

          payload = this.getInventoryTablePayload();

          if (this.receipt) {
            payload['receipt_uuid'] = this.receipt.uuid
          } else {
            if (!payload.customer_uuid && !payload.receipt_uuid) {
              this.$errorDialog(this.$i18n.t("Please, select customer or receipt in the filters and try again"), this.$i18n.t('Inventory Overview Validation'))
              return
            }
          }

        } else {
          payload = {'uuids': [...items].map(item => item.uuid).join(',')};
        }

        if (Object.keys(this.locales.available_locales).length === 1) {
          let success = await this.$confirm(this.$i18n.t("Are you sure you want to export inventory overview with following filters?"))
          if (!success) {
            return;
          }

          let response = await InventoryService.exportInventoryOverview({
            ...payload, language: this.locales.master_language.key
          })
          if (response.success) {
            this.$toast('success', this.$i18n.t("Inventory overview export has successfully started."))
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0])
            } else {
              this.$errorDialog(this.$i18n.t("Something went wrong. Please, try again later"))
            }
          }
        } else {
          this.showInventoryOverviewGenerationModal({payload: payload})
        }
      },
      onBatchUpdateReceiptClick(items) {
        if (items.size > 200) {
          this.$errorDialog(this.$i18n.t("You can not move more than {num} items.", {num: 200}))
          return false;
        }

        let uuids = [];
        let groupedItems = [];
        items.forEach(item => {
          if (item.is_grouped_item) {
            groupedItems.push(item.stock_no);
          }
          uuids.push(item.uuid);
        });
        if (groupedItems.length > 0) {
          this.$errorDialog(this.$i18n.t("Receipt can't be changed for grouped items") + ': ' + groupedItems.join(', '));
          return;
        }
        this.showBatchUpdateReceiptModal({uuids});
      },
      onTransferAcrossBranchesClick(items) {
        if (items.size > 200) {
          this.$errorDialog(this.$i18n.t("You can not transfer more than {num} items.", {num: 200}))
          return false;
        }

        let uuids = [];
        const [firstItem] = items;
        const firstReceiptUuid = firstItem.receipt_uuid;
        const allowedStatuses = ['draft', 'unsold', 'withdrawn'];
        let differentReceipts = false;
        const errorStatusStockNos = new Set();
        let archivedItems = [];
        items.forEach(item => {
          if (item.receipt_uuid !== firstReceiptUuid) {
            differentReceipts = true;
          }
          if (item.is_archived) {
            archivedItems.push(`${this.$options.filters.striptags(Formatter.attributeByMasterLanguage(item.dynamic_fields, 'title'))} (${item.stock_no})`);
          }
          for (const status of item.statuses) {
            if (!allowedStatuses.includes(status.status) || item.is_archived) {
              errorStatusStockNos.add(item.stock_no);
              break;
            }
          }
          uuids.push(item.uuid);
        });
        if (archivedItems.length > 0) {
          const errorText = this.$i18n.t("The following items are archived and can not be processed");
          this.$errorDialog(errorText + ': ' + archivedItems.join(', '));
          return;
        }
        if (differentReceipts || errorStatusStockNos.size > 0) {
          const receiptErrorText = this.$i18n.t("Items selected for branch transfer come from different receipts. Please select items that belong to the same receipt.");
          const statusErrorText = this.$i18n.t("Some of the selected items do not have Draft, Unsold, or Withdrawn statuses. Please ensure that only items with these statuses are included.");

          if (!differentReceipts) {
            this.$errorDialog(statusErrorText + ' ' + [...errorStatusStockNos].join(', '));
            return;
          }
          if (errorStatusStockNos.size === 0) {
            this.$errorDialog(receiptErrorText);
            return;
          }

          const listItemsArr = [];
          listItemsArr.push(this.$createElement('li', {}, receiptErrorText));
          listItemsArr.push(this.$createElement('li', {}, statusErrorText + ' ' + [...errorStatusStockNos].join(', ')));

          this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Error'));
          return;
        }
        let branchUuids = this.usersOtherBranches.map(branch => branch.uuid);
        this.showTransferAcrossBranchesModal({uuids, branchUuids, customerUuid: firstItem.customer_uuid});
      },
      async onViewDpvPdfClick(dpvUuid) {
        await DpvService.getDpvPdfUrl(this, dpvUuid);
      },
      getItemLifecycleDisplayValue(itemLifecycle) {
        if (!itemLifecycle || !this.itemLifecycleOptions) {
          return itemLifecycle;
        }
        const option = this.itemLifecycleOptions.find(opt => opt.key === itemLifecycle);
        return option ? option.value : itemLifecycle;
      },
      async onAttachmentsViewClick(inventory) {
        await this.updateAttachmentsCurrentTab(1)
        this.$router.push({name: 'inventory.attachments', params: {id: inventory.uuid}})
      }
    },
    watch: {
      'tableData': {
        deep: true,
        handler() {
          this.hasImages();
        }
      }
    },
    mounted() {
      this.getTableData();
      this.hasImages();
      this.$ws.listen(`inventories.${WS_DATA_COLLECTION_PROVIDER_PROGRESS}`, this.onProgressNotificationReceive);
      this.$ws.listen(`inventories.${WS_TRANSFER_ACROSS_BRANCHES_PROGRESS}`, async (data) => {
        await this.addProgressIfNotExist(this.$t('Transfer Across Branches'), 'transferAcrossBranchesProgress');
        this.handleProgressNotification(
          data,
          this.$t('The items have been successfully transferred to the {branch_name} branch under receipt {receipt_no}.', {
            branch_name: data.branch_name,
            receipt_no: data.receipt_no
          }),
          this.$t('Transfer Across Branches'),
          this.$t('Error while transferring inventory items across branches. Please, try again later'),
          'transferAcrossBranchesProgress'
        )
      });
      this.$ws.listen(`inventories.${WS_BATCH_UPDATE_RECEIPT_PROGRESS}`, async (data) => {
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
        await this.addProgressIfNotExist(this.$t('Move Items to Another Receipt'), 'batchUpdateReceiptProgress');
        this.handleProgressNotification(
          data,
          this.$t('The items have been successfully moved to receipt') + ' ' + (data.receipt_no || '') + '.',
          this.$t('Move Items to Another Receipt'),
          this.$t('Error while moving inventory items to another receipt. Please, try again later'),
          'batchUpdateReceiptProgress'
        )
      });
    },
    beforeDestroy() {
      this.removeProgressIfExist();
      this.removeProgressIfExist('transferAcrossBranchesProgress');
      this.removeProgressIfExist('batchUpdateReceiptProgress');
      this.$ws.remove(`inventories.${WS_DATA_COLLECTION_PROVIDER_PROGRESS}`);
      this.$ws.remove(`inventories.${WS_TRANSFER_ACROSS_BRANCHES_PROGRESS}`);
      this.$ws.remove(`inventories.${WS_BATCH_UPDATE_RECEIPT_PROGRESS}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .attachments-numbering {
    z-index: 10;
    top: -3px;
    right: -7px;
    margin: 0;
    border-radius: 100%;
    padding: 1.5px 3px;
    font-size: 10px;
    position: absolute;
    background-color: #1ec973;
    color: white;
  }

  .artisio-table {
    /deep/ {
      .stock-no-col {
        min-width: 150px;
        width: 170px;
      }

      .title-col {
        min-width: 320px;
        max-width: 500px;
        text-wrap: wrap;
      }
      .description-col {
        min-width: 320px;
        max-width: 500px;
        text-wrap: wrap;
      }

      .estimate-col {
        min-width: 160px;
        width: 180px;
      }

      .quantity-col {
        min-width: 90px;
        width: 110px;
      }

      .grouped_item_no-col {
        min-width: 110px;
        width: 110px;
      }

      .reserve-col {
        min-width: 80px;
        width: 100px;
      }

      .receive-date-col {
        min-width: 130px;
        width: 150px;
      }

      .status-col {
        min-width: 140px;
        width: 160px;
      }

      .account-managers-col {
        min-width: 140px;
      }

      .customer-col {
        min-width: 140px;
      }

      .tax-status-col {
        min-width: 100px;
      }

      .department-col {
        min-width: 100px;
      }

      .category-col {
        min-width: 100px;
      }

      .item-location-col {
        min-width: 100px;
      }

      .start-price-col {
        min-width: 100px;
      }

      .collection-info-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .customer-col {
        min-width: 100px;
      }

      .tax-rate-col {
        min-width: 100px;
      }

      .dynamic-fields-col {
        min-width: 100px;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
</style>

